import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * A full-screen sheet for phones (the order ticket, the account, the notification log…): a native modal dialog, so focus
 * stays inside and the back gesture or Escape closes it.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={onClose}
      className="m-0 h-[100dvh] max-h-none w-screen max-w-none bg-panel p-0 text-fg backdrop:bg-black/60"
    >
      {open && (
        <div className="flex h-full flex-col pb-[env(safe-area-inset-bottom)]">
          <header className="flex items-center gap-2 border-b border-line px-3 py-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
            <h2 id={titleId} className="flex-1 text-base font-semibold">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line px-3 py-1.5 text-sm hover:border-accent"
            >
              Close
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-auto">{children}</div>
        </div>
      )}
    </dialog>
  );
}
